'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import { Theme, createTheme } from '@mui/material/styles';

import { LinkProps } from '../shared/Link';
import { DialogsProvider } from '../useDialogs';
import { NotificationsProvider } from '../useNotifications';
import {
  RouterContext,
  WindowContext,
  BrandingContext,
  NavigationContext,
} from '../shared/context';

import { AppThemeProvider } from './AppThemeProvider';
import { type LocaleText, LocalizationProvider } from './LocalizationProvider';

export interface NavigateOptions {
  history?: 'auto' | 'push' | 'replace';
}

export interface Navigate {
  (url: string | URL, options?: NavigateOptions): void;
}

/**
 * Abstract router used by Toolpad components.
 */
export interface Router {
  pathname: string;
  navigate: Navigate;
  searchParams: URLSearchParams;
  Link?: React.ComponentType<LinkProps>;
}

export interface Branding {
  title?: string;
  homeUrl?: string;
  logo?: React.ReactNode;
}

export interface NavigationPageItem {
  kind?: 'page';
  title?: string;
  segment?: string;
  pattern?: string;
  children?: Navigation;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export interface NavigationSubheaderItem {
  title: string;
  kind: 'header';
}

export interface NavigationDividerItem {
  kind: 'divider';
}

export type NavigationItem =
  NavigationPageItem | NavigationDividerItem | NavigationSubheaderItem;

export type Navigation = NavigationItem[];

export interface Session {
  user?: {
    id?: string | null;
    name?: string | null;
    image?: string | null;
    email?: string | null;
  };
}

export interface Authentication {
  signIn: () => void;
  signOut: () => void;
}

export const AuthenticationContext = React.createContext<Authentication | null>(
  null
);

export const SessionContext = React.createContext<Session | null>(null);

export type AppTheme = Theme | { dark: Theme; light: Theme };

export interface AppProviderProps {
  /**
   * The nonce to be used for inline scripts.
   */
  nonce?: string;
  /**
   * Router implementation used inside Toolpad components.
   * @default null
   */
  router?: Router;
  /**
   * The window where the application is rendered.
   * This is needed when rendering the app inside an iframe, for example.
   * @default window
   */
  window?: Window;
  /**
   * [Theme or themes](https://mui.com/toolpad/core/react-app-provider/#theming) to be used by the app in light/dark mode. A [CSS variables theme](https://mui.com/material-ui/customization/css-theme-variables/overview/) is recommended.
   * @default createDefaultTheme()
   */
  theme?: AppTheme;
  /**
   * Navigation definition for the app. [Find out more](https://mui.com/toolpad/core/react-app-provider/#navigation).
   * @default []
   */
  navigation?: Navigation;
  /**
   * Session info about the current user.
   * @default null
   */
  session?: Session | null;
  /**
   * The content of the app provider.
   */
  children: React.ReactNode;
  /**
   * Branding options for the app.
   * @default null
   */
  branding?: Branding | null;
  /**
   * Locale text for components
   */
  localeText?: Partial<LocaleText>;
  /**
   * Authentication methods.
   * @default null
   */
  authentication?: Authentication | null;
}

function createDefaultTheme(): Theme {
  return createTheme({
    colorSchemes: { dark: true },
    cssVariables: {
      colorSchemeSelector: 'data-toolpad-color-scheme',
    },
  });
}

/**
 *
 * Demos:
 *
 * - [App Provider](https://mui.com/toolpad/core/react-app-provider/)
 * - [Dashboard Layout](https://mui.com/toolpad/core/react-dashboard-layout/)
 *
 * API:
 *
 * - [AppProvider API](https://mui.com/toolpad/core/api/app-provider)
 */
function AppProvider(props: AppProviderProps) {
  const {
    nonce,
    children,
    localeText,
    router = null,
    session = null,
    branding = null,
    navigation = [],
    window: appWindow,
    authentication = null,
    theme = createDefaultTheme(),
  } = props;

  return (
    <WindowContext.Provider value={appWindow}>
      <AuthenticationContext.Provider value={authentication}>
        <SessionContext.Provider value={session}>
          <RouterContext.Provider value={router}>
            <AppThemeProvider theme={theme} nonce={nonce} window={appWindow}>
              <LocalizationProvider localeText={localeText}>
                <NotificationsProvider>
                  <DialogsProvider>
                    <BrandingContext.Provider value={branding}>
                      <NavigationContext.Provider value={navigation}>
                        {children}
                      </NavigationContext.Provider>
                    </BrandingContext.Provider>
                  </DialogsProvider>
                </NotificationsProvider>
              </LocalizationProvider>
            </AppThemeProvider>
          </RouterContext.Provider>
        </SessionContext.Provider>
      </AuthenticationContext.Provider>
    </WindowContext.Provider>
  );
}

AppProvider.propTypes /* remove-proptypes */ = {
  /**
   * The nonce to be used for inline scripts.
   */
  nonce: PropTypes.string,
  /**
   * [Theme or themes](https://mui.com/toolpad/core/react-app-provider/#theming) to be used by the app in light/dark mode. A [CSS variables theme](https://mui.com/material-ui/customization/css-theme-variables/overview/) is recommended.
   * @default createDefaultTheme()
   */
  theme: PropTypes.object,
  /**
   * The content of the app provider.
   */
  children: PropTypes.node,
  /**
   * The window where the application is rendered.
   * This is needed when rendering the app inside an iframe, for example.
   * @default window
   */
  window: PropTypes.object,
  /**
   * Locale text for components
   */
  localeText: PropTypes.object,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Authentication methods.
   * @default null
   */
  authentication: PropTypes.shape({
    signIn: PropTypes.func.isRequired,
    signOut: PropTypes.func.isRequired,
  }),
  /**
   * Branding options for the app.
   * @default null
   */
  branding: PropTypes.shape({
    logo: PropTypes.node,
    title: PropTypes.string,
    homeUrl: PropTypes.string,
  }),
  /**
   * Session info about the current user.
   * @default null
   */
  session: PropTypes.shape({
    user: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      email: PropTypes.string,
      image: PropTypes.string,
    }),
  }),
  /**
   * Router implementation used inside Toolpad components.
   * @default null
   */
  router: PropTypes.shape({
    Link: PropTypes.elementType,
    navigate: PropTypes.func.isRequired,
    pathname: PropTypes.string.isRequired,
    searchParams: PropTypes.instanceOf(URLSearchParams).isRequired,
  }),
  /**
   * Navigation definition for the app. [Find out more](https://mui.com/toolpad/core/react-app-provider/#navigation).
   * @default []
   */
  navigation: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        icon: PropTypes.node,
        action: PropTypes.node,
        title: PropTypes.string,
        pattern: PropTypes.string,
        segment: PropTypes.string,
        kind: PropTypes.oneOf(['page']),
        children: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.shape({
              title: PropTypes.string.isRequired,
              kind: PropTypes.oneOf(['header']).isRequired,
            }),
            PropTypes.shape({
              kind: PropTypes.oneOf(['divider']).isRequired,
            }),
          ]).isRequired
        ),
      }),
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        kind: PropTypes.oneOf(['header']).isRequired,
      }),
      PropTypes.shape({
        kind: PropTypes.oneOf(['divider']).isRequired,
      }),
    ]).isRequired
  ),
} as any;

export { AppProvider };
